import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import Apple from "@/app/assets/icons/apple";
import { cn } from "@/lib/utils";
import Close from "@/app/assets/icons/close";

const BASE_BADGE_CLASSES =
    "inline-flex items-center justify-center gap-s3 rounded-md font-md py-s3 text-xs font-medium";

export enum BadgeVariant {
    Primary = "primary",
    Error = "error",
    Warning = "warning",
    Success = "success",
    Info = "info",
    Default = "default",
}

export enum BadgeSize {
    Small = "sm",
    Large = "lg",
}

export enum BadgeScheme {
    Dark = "dark",
    Light = "light",
    Outline = "outline",
}

const VARIANT_STYLES_LIGHT = {
    [BadgeVariant.Default]: " border-none bg-neutral-50 text-neutral-900 ",
    [BadgeVariant.Error]: " border-none bg-errorOpacity-300 text-error-600 ",
    [BadgeVariant.Info]: "border-none bg-infoOpacity-100 text-info-500 ",
    [BadgeVariant.Primary]: " border-none text-brand-primary-500  bg-primaryOpacity-200",
    [BadgeVariant.Success]: "border-none bg-successOpacity-100 text-success-700 ",
    [BadgeVariant.Warning]: "border-none bg-warningOpacity-100 text-warning-700 ",
};

const VARIANT_STYLES_DARK = {
    [BadgeVariant.Default]: "border-none bg-neutral-800 text-white ",
    [BadgeVariant.Error]: " border-none bg-error-500 text-white ",
    [BadgeVariant.Info]: "border-none bg-info-500 text-white ",
    [BadgeVariant.Primary]: " border-none bg-brand-primary-500 text-white ",
    [BadgeVariant.Success]: "border-none bg-success-500 text-white ",
    [BadgeVariant.Warning]: "border-none bg-warning-500 text-white ",
};

const VARIANT_STYLES_OUTLINE = {
    [BadgeVariant.Default]: "border border-opacityDark-10 text-neutral-900 ",
    [BadgeVariant.Error]: " border border-error-600 text-error-600 ",
    [BadgeVariant.Info]: "border border-info-500 text-info-500 ",
    [BadgeVariant.Primary]: " border border-brand-primary-500  text-brand-primary-500",
    [BadgeVariant.Success]: " border border-success-700 text-success-700 ",
    [BadgeVariant.Warning]: " border border-warning-700 text-warning-700 ",
};

const BADGE_SCHEME_STYLE = {
    [BadgeScheme.Dark]: VARIANT_STYLES_DARK,
    [BadgeScheme.Light]: VARIANT_STYLES_LIGHT,
    [BadgeScheme.Outline]: VARIANT_STYLES_OUTLINE,
};

const SIZE_STYLES = {
    [BadgeSize.Small]: "text-xs max-w-fit tracking-[0.12px]",
    [BadgeSize.Large]: "text-sm max-w-fit tracking-[0.14px]",
};

const IMAGE_SIZE_STYLES = {
    [BadgeSize.Small]: "w-s6 h-s6",
    [BadgeSize.Large]: "w-s7 h-s7",
};

const badgeVariants = cva(BASE_BADGE_CLASSES, {
    variants: {
        variant: {
            ...VARIANT_STYLES_LIGHT,
            ...VARIANT_STYLES_DARK,
            ...VARIANT_STYLES_OUTLINE,
        },
        size: SIZE_STYLES,
        scheme: {
            [BadgeScheme.Light]: "",
            [BadgeScheme.Dark]: "",
            [BadgeScheme.Outline]: "",
        },
        padding: {
            default: "px-s5",
            compact: "px-s3",
        },
    },
    compoundVariants: [
        ...Object.entries(BADGE_SCHEME_STYLE).flatMap(([scheme, variants]) =>
            Object.entries(variants).map(([variant, className]) => ({
                scheme: scheme as BadgeScheme,
                variant: variant as BadgeVariant,
                className,
            })),
        ),
    ],
    defaultVariants: {
        variant: BadgeVariant.Primary,
        size: BadgeSize.Small,
        scheme: BadgeScheme.Light,
        padding: "default",
    },
});

export interface BadgeProps
    extends React.HTMLAttributes<HTMLDivElement>,
        VariantProps<typeof badgeVariants> {
    icon?: React.ReactElement;
    label?: string;
    showIcon?: boolean;
    showCross?: boolean;
    showLabel?: boolean;
    scheme?: BadgeScheme;
    onClose?: () => void;
    rightIcon?: React.ReactElement;
    showRightIcon?: boolean;
    onRightIconClick?: () => void;
}

function Badge({
    className,
    variant = BadgeVariant.Primary,
    size = BadgeSize.Small,
    scheme = BadgeScheme.Light,
    icon = <Apple />,
    showIcon = true,
    showCross = true,
    showLabel = true,
    label = "Label",
    rightIcon = <Apple />,
    showRightIcon= false,
    onRightIconClick,
    onClose,
    ...props
}: BadgeProps) {
    const imageSizeStyle = size ? IMAGE_SIZE_STYLES[size] : "";
    const padding = showIcon || showCross ? "compact" : "default";

    const styledIcon = icon ? (
        React.cloneElement(icon, {
            className: cn(imageSizeStyle),
        })
    ) : (
        <></>
    );

    const rightIconStyle = rightIcon ? (
        React.cloneElement(rightIcon, {
            className: cn(imageSizeStyle),
            onClick: {onRightIconClick}
        })
    ) : (
        <></>
    );

    return (
        <div
            className={cn(badgeVariants({ variant, size, scheme, padding }), className)}
            {...props}
        >
            {showIcon && styledIcon}
            {showLabel && label}
            {showRightIcon && rightIconStyle }
            {showCross && <Close onClick={onClose} className={cn(imageSizeStyle)} />}
        </div>
    );
}

export { Badge, badgeVariants };
